import React from 'react';

import type { FormField } from '../types/pdf.types';

interface CheckboxFieldOverlayProps {
  field: FormField;
  checked: boolean;
  onChange: (checked: boolean) => void;
  scale: number;
  isZooming?: boolean;
}

const CheckboxFieldOverlay: React.FC<CheckboxFieldOverlayProps> = ({
  field,
  checked,
  onChange,
  scale,
  isZooming = false,
}) => {
  if (field.hidden) return null;

  const [x, y, width, height] = field.rect;
  const scaledX = x * scale;
  const scaledY = y * scale;
  const scaledWidth = width * scale;
  const scaledHeight = height * scale;

  const isReadOnly = field.readOnly === true;
  const isRequired = field.required === true;
  const isNoExport = field.noExport === true;

  const overlayStyle = {
    left: `${scaledX}px`,
    top: `${scaledY}px`,
    width: `${scaledWidth}px`,
    height: `${scaledHeight}px`,
    opacity: isZooming ? 0 : 1,
    pointerEvents:
      isZooming || isReadOnly ? ('none' as const) : ('auto' as const),
    cursor: isReadOnly ? 'not-allowed' : 'pointer',
  };

  const overlayClassName = [
    'pdf-field-overlay',
    'pdf-field-toggle',
    isRequired ? 'pdf-field-toggle--required' : '',
    isNoExport ? 'pdf-field-toggle--no-export' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const titleParts = [field.name];
  if (isRequired) titleParts.push('Required');
  if (isNoExport) titleParts.push('No export');
  if (isReadOnly) titleParts.push('Read-only');
  const title = titleParts.join(' • ');

  return (
    <input
      type="checkbox"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      className={overlayClassName}
      style={overlayStyle}
      title={title}
      disabled={isReadOnly}
      required={isRequired}
      aria-required={isRequired}
    />
  );
};

const areEqual = (
  prevProps: CheckboxFieldOverlayProps,
  nextProps: CheckboxFieldOverlayProps,
) => {
  return (
    prevProps.field.id === nextProps.field.id &&
    prevProps.field.readOnly === nextProps.field.readOnly &&
    prevProps.checked === nextProps.checked &&
    prevProps.scale === nextProps.scale &&
    prevProps.isZooming === nextProps.isZooming
  );
};

export default React.memo(CheckboxFieldOverlay, areEqual);
